import { useState } from "react";
import { useSnackbar } from "notistack";
// @mui
import { Box, Button, Stack, styled, useTheme } from "@mui/material";
// hooks
import useResponsive from "~src/hooks/useResponsive";
// components
import Iconify from "~src/components/Iconify";
import CarouselButtons from "~src/components/CarouselButtons";
import ContentBox from "~src/components/ContentBox";
import AdContent from "./AdContent";
// types
import { TrendingAd } from "~src/types/ad";

//-----------------------------------------------------------------------------------------------------

type PinnedAdDetailModalProps = {
  handleClose: VoidFunction;
  onNext: VoidFunction;
  onPrevious: VoidFunction;
  ad?: TrendingAd;
};

const FooterButton = styled(Button)(({ theme }) => ({
  borderRadius: 100,
  border: `1px solid ${theme.palette.grey[300]}`,
  color: theme.palette.grey[700],
  fontSize: 14,
  lineHeight: "20px",
  fontWeight: 400,
  width: 153,
  height: 40,
  [theme.breakpoints.down("md")]: {
    width: 105,
    height: 30,
    fontSize: 10,
    ".MuiButton-startIcon": {
      width: 15,
      height: 15,
      marginBottom: 3,
    },
  },
}));

// Helper function to get asset URL from ad
const getAssetUrl = (ad?: TrendingAd) => {
  if (!ad) return undefined;
  const getImageUrl = (urlString?: string | null) => {
    return urlString && urlString !== "N/A" ? urlString : undefined;
  };
  const imageUrlString = getImageUrl(ad.ad_image);
  if (imageUrlString) {
    return imageUrlString;
  }
  return ad.ad_video;
};

export default function AdDetailPreviewModal({
  handleClose,
  onNext,
  onPrevious,
  ad,
}: PinnedAdDetailModalProps) {
  const theme = useTheme();
  const isDesktop = useResponsive("up", "md");
  const { enqueueSnackbar } = useSnackbar();
  const [focusedAssetUrl, setFocusedAssetUrl] = useState<
    string | undefined | null
  >(() => getAssetUrl(ad));
  const [shareLink, setShareLink] = useState<string>();
  const [focusedCtaUrl, setFocusedUrl] = useState<string | undefined | null>(
    () => ad?.ad_link
  );
  const [prevAd, setPrevAd] = useState(ad);

  // Sync state from prop during render (not in useEffect) to avoid extra render cycle
  if (ad !== prevAd) {
    setPrevAd(ad);
    if (ad) {
      setFocusedUrl(ad.ad_link);
      setFocusedAssetUrl(getAssetUrl(ad));
    }
  }

  if (!ad) {
    return null;
  }

  const shareLinkClicked = () => {
    if (ad.ad_link) {
      navigator.clipboard.writeText(ad.ad_link);
      setShareLink(ad.ad_link);
      enqueueSnackbar("Link copied to clipboard");
    }
  };

  return (
    <ContentBox>
      <AdContent
        ad={ad}
        handleClose={handleClose}
        focusedCtaUrl={focusedCtaUrl}
      />
      <Box
        sx={{
          width: "100%",
          mt: "20px",
          display: "flex",
          flexDirection: "row",
          [theme.breakpoints.up("md")]: {
            justifyContent: "space-between",
          },
        }}
      >
        <Stack direction="row" spacing={2}>
          {focusedAssetUrl && (
            <FooterButton
              sx={{
                ".MuiButton-startIcon": {
                  mr: { xs: "2px", md: 1 },
                },
              }}
              startIcon={<Iconify icon={"system-uicons:download"} />}
              onClick={() => {
                window.open(focusedAssetUrl, "_blank");
              }}
            >
              Download Asset
            </FooterButton>
          )}
          {focusedCtaUrl && (
            <FooterButton
              startIcon={<Iconify icon={"solar:monitor-linear"} />}
              onClick={() => window.open(focusedCtaUrl, "_blank")}
            >
              Landing Page
            </FooterButton>
          )}
          <FooterButton
            onClick={shareLinkClicked}
            startIcon={<Iconify icon={"ri:links-line"} />}
          >
            {shareLink ? "Link copied to clipboard" : "Copy ad link"}
          </FooterButton>
        </Stack>
        {isDesktop && (
          <CarouselButtons onSwipeLeft={onPrevious} onSwipeRight={onNext} />
        )}
      </Box>
      {!isDesktop && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <CarouselButtons
            sx={{
              "& .MuiButton-root": {
                color: theme.palette.grey[300],
                backgroundColor: theme.palette.common.white,
                border: `1px solid ${theme.palette.grey[300]}`,
                height: 30,
                minWidth: 36,
              },
            }}
            onSwipeLeft={onPrevious}
            onSwipeRight={onNext}
          />
        </Box>
      )}
    </ContentBox>
  );
}
